import { useState, useEffect, useContext } from 'react';
import { FetchContext } from '@src/scenario-editor/providers/fetch.context';
export type { FetchProfileManagementPayload } from '@src/scenario-editor/providers/fetch.context';

export interface ProfileField {
  id: string;
  label: string;
  fieldType: string;
}

// LF360-1828: fetchProfileManagement() strips the 'message:' prefix off nodeType
// ('message:single' | 'message:multi' | 'message:input' | 'message:picker' |
// 'message:date' | 'message:time' - the same enum lf360-be uses for
// SubjectProfileField.inputType) down to the short fieldType vocabulary
// (single|multi|input|picker|date|time) the editor's operator/value-control
// mapping switches on (see BehaviorRulesNodeInfo.tsx's PROFILE_FIELD_OPERATOR_OPTIONS).
const toShortFieldType = (nodeType: string): string => nodeType.replace(/^message:/, '');

// LF360-1828: REUSES fetchProfileManagement() (see fetch.context.tsx) rather than
// adding a new fetcher, per the existing node-deletion guard's contract
// (useAllowDeletion.hook.ts). Its payload item shape - {type, nodeId, message,
// nodeType, sourceId} - was built for that node-deletion check, not as a
// purpose-built "profile field" record; sourceId/message are read here as the
// field's identifying key + display label. See task-4-report.md for the caveat
// this depends on: the fe wiring must key sourceId by the SubjectProfileField's
// own id (not the bound scenario/onboarding/task id) for the persisted
// profileFieldId to resolve correctly on the backend.
export const useFetchProfileFields = () => {
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [isLoading, setIsLoading] = useState<null | boolean>(null);
  const fetchContext = useContext(FetchContext);

  if (!fetchContext) {
    throw new Error('FetchContext is not provided');
  }

  useEffect(() => {
    const fetchProfileFields = async () => {
      try {
        setIsLoading(true);
        const profileManagement = await fetchContext.fetchProfileManagement();

        const deduped = new Map<string, ProfileField>();
        (profileManagement.data ?? []).forEach((item) => {
          if (!item?.sourceId || deduped.has(item.sourceId)) {
            return;
          }
          deduped.set(item.sourceId, {
            id: item.sourceId,
            label: item.message,
            fieldType: toShortFieldType(item.nodeType),
          });
        });

        setProfileFields(Array.from(deduped.values()));
        setIsLoading(false);
      } catch (e) {
        console.error(e);
      }
    };
    fetchProfileFields().catch(console.error);
  }, [fetchContext]);

  return [profileFields, isLoading];
};
